import React from 'react'
import { TextInput as MantineTextInput } from '@mantine/core'
import type { UseFormRegisterReturn } from 'react-hook-form'

interface TimeInputProps {
  label: string
  required?: boolean
  error?: string
  helperText?: string
  className?: string
  disabled?: boolean
  readOnly?: boolean
  register?: UseFormRegisterReturn
  value?: string
  onChange?: (value: string) => void
  format?: 'HH:MM:SS' | 'HH:MM'
}

export function TimeInput({
  label,
  required = false,
  error,
  helperText,
  className = '',
  disabled = false,
  readOnly = false,
  register,
  value,
  onChange,
  format = 'HH:MM:SS',
}: TimeInputProps) {
  const [hours, setHours] = React.useState('00')
  const [minutes, setMinutes] = React.useState('00')
  const [seconds, setSeconds] = React.useState('00')

  React.useEffect(() => {
    const currentValue = value || ''
    if (currentValue) {
      const parts = currentValue.split(':')
      if (parts.length >= 2) {
        setHours(parts[0] || '00')
        setMinutes(parts[1] || '00')
        setSeconds(parts[2] || '00')
      }
    }
  }, [value])

  const handleTimeChange = (
    newHours: string,
    newMinutes: string,
    newSeconds: string
  ) => {
    setHours(newHours)
    setMinutes(newMinutes)
    setSeconds(newSeconds)

    const timeString =
      format === 'HH:MM:SS'
        ? `${newHours}:${newMinutes}:${newSeconds}`
        : `${newHours}:${newMinutes}`

    if (register) {
      register.onChange({ target: { value: timeString } })
    } else if (onChange) {
      onChange(timeString)
    }
  }

  const validateTime = (val: string, max: number) => {
    const num = parseInt(val, 10)
    return !isNaN(num) && num >= 0 && num <= max
  }

  const handleHoursChange = (val: string) => {
    if (readOnly) return
    // Allow empty string or any valid input up to 2 characters
    if (val === '' || (val.length <= 2 && /^\d*$/.test(val))) {
      // Only validate the final value if it's 2 characters or if it's a single digit
      if (val.length === 2) {
        if (validateTime(val, 23)) {
          handleTimeChange(val, minutes, seconds)
        }
      } else if (val.length === 1) {
        // Allow single digits, they'll be padded when we lose focus
        handleTimeChange(val, minutes, seconds)
      } else if (val === '') {
        handleTimeChange('00', minutes, seconds)
      }
    }
  }

  const handleMinutesChange = (val: string) => {
    if (readOnly) return
    // Allow empty string or any valid input up to 2 characters
    if (val === '' || (val.length <= 2 && /^\d*$/.test(val))) {
      // Only validate the final value if it's 2 characters or if it's a single digit
      if (val.length === 2) {
        if (validateTime(val, 59)) {
          handleTimeChange(hours, val, seconds)
        }
      } else if (val.length === 1) {
        // Allow single digits, they'll be padded when we lose focus
        handleTimeChange(hours, val, seconds)
      } else if (val === '') {
        handleTimeChange(hours, '00', seconds)
      }
    }
  }

  const handleSecondsChange = (val: string) => {
    if (readOnly) return
    // Allow empty string or any valid input up to 2 characters
    if (val === '' || (val.length <= 2 && /^\d*$/.test(val))) {
      // Only validate the final value if it's 2 characters or if it's a single digit
      if (val.length === 2) {
        if (validateTime(val, 59)) {
          handleTimeChange(hours, minutes, val)
        }
      } else if (val.length === 1) {
        // Allow single digits, they'll be padded when we lose focus
        handleTimeChange(hours, minutes, val)
      } else if (val === '') {
        handleTimeChange(hours, minutes, '00')
      }
    }
  }

  return (
    <div className={`space-y-1 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="flex items-center space-x-2">
        <MantineTextInput
          value={hours}
          onChange={e => handleHoursChange(e.target.value)}
          onFocus={e => {
            if (!readOnly) e.target.select()
          }}
          onBlur={() => {
            if (hours.length === 1) {
              handleTimeChange(hours.padStart(2, '0'), minutes, seconds)
            }
          }}
          disabled={disabled}
          readOnly={readOnly}
          placeholder="HH"
          size="md"
          radius="md"
          className="w-16 text-center"
          maxLength={2}
          styles={{
            input: {
              backgroundColor: '#F5F5F7',
              border: 'none',
            },
            error: {
              fontSize: 10,
            },
          }}
        />
        <span className="text-gray-500 font-medium">:</span>

        <MantineTextInput
          value={minutes}
          onChange={e => handleMinutesChange(e.target.value)}
          onFocus={e => {
            if (!readOnly) e.target.select()
          }}
          onBlur={() => {
            if (minutes.length === 1) {
              handleTimeChange(hours, minutes.padStart(2, '0'), seconds)
            }
          }}
          disabled={disabled}
          readOnly={readOnly}
          placeholder="MM"
          size="md"
          radius="md"
          className="w-16 text-center"
          maxLength={2}
          styles={{
            input: {
              backgroundColor: '#F5F5F7',
              border: 'none',
            },
            error: {
              fontSize: 10,
            },
          }}
        />

        {format === 'HH:MM:SS' && (
          <>
            <span className="text-gray-500 font-medium">:</span>
            <MantineTextInput
              value={seconds}
              onChange={e => handleSecondsChange(e.target.value)}
              onFocus={e => {
                if (!readOnly) e.target.select()
              }}
              onBlur={() => {
                if (seconds.length === 1) {
                  handleTimeChange(hours, minutes, seconds.padStart(2, '0'))
                }
              }}
              disabled={disabled}
              readOnly={readOnly}
              placeholder="SS"
              size="md"
              radius="md"
              className="w-16 text-center"
              maxLength={2}
              styles={{
                input: {
                  backgroundColor: '#F5F5F7',
                  border: 'none',
                },
                error: {
                  fontSize: 10,
                },
              }}
            />
          </>
        )}
      </div>

      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}

      {helperText && <p className="text-sm text-gray-500 mt-1">{helperText}</p>}
    </div>
  )
}
